import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Modal, Tooltip } from '../../components';
import * as investorService from '../../services/investor-service';
import { getCurrentUser } from '../../data/session';
import type { PublishedPitch } from '../../services/investor-service';

// ──────────────────────────────────────────────────────────────────────────
// Shared helpers
// ──────────────────────────────────────────────────────────────────────────

const PASSED_KEY = 'investor_passed_pitches';

const readPassed = (): Set<string> => {
  try {
    const raw = localStorage.getItem(PASSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const writePassed = (s: Set<string>): void => {
  try {
    localStorage.setItem(PASSED_KEY, JSON.stringify(Array.from(s)));
  } catch {}
};

const industryColor: Record<string, string> = {
  Technology: 'bg-chart/10 text-chart border-chart/20',
  HealthTech: 'bg-success/10 text-success border-success/20',
  EdTech: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  Logistics: 'bg-warning/10 text-warning border-warning/20',
  Finance: 'bg-warning/10 text-warning border-warning/20',
  CleanTech: 'bg-chart/10 text-chart border-chart/20',
  AgriTech: 'bg-success/10 text-success border-success/20',
};

const stageLabel: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  growth: 'Growth',
  scale: 'Scale',
  'pre-seed': 'Pre-Seed',
  seed: 'Seed',
  'series-a': 'Series A',
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// ──────────────────────────────────────────────────────────────────────────
// Small sub-components
// ──────────────────────────────────────────────────────────────────────────

const SectionCard: React.FC<{ label: string; value?: string; accent?: boolean }> = ({ label, value, accent }) => {
  if (!value) return null;
  return (
    <section
      className={`rounded-card p-6 border ${
        accent ? 'bg-chart/5 border-chart/20' : 'bg-shell border-line'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted mb-3">{label}</div>
      <p className="text-text text-sm leading-relaxed whitespace-pre-wrap">{value}</p>
    </section>
  );
};

const StatCell: React.FC<{ label: string; value: string; accent?: 'text' | 'chart' }> = ({ label, value, accent = 'text' }) => (
  <div className="flex flex-col gap-1">
    <div className="text-[10px] uppercase tracking-widest text-muted font-semibold">{label}</div>
    <div className={`font-display text-3xl md:text-4xl tabular-nums leading-none ${accent === 'chart' ? 'text-chart' : 'text-text'}`}>
      {value || '—'}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const PitchView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pitch, setPitch] = useState<PublishedPitch | null>(null);
  const [loading, setLoading] = useState(true);

  const [expressing, setExpressing] = useState(false);
  const [expressed, setExpressed] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passedSet, setPassedSet] = useState<Set<string>>(() => readPassed());
  const isPassed = id ? passedSet.has(id) : false;

  // Session request modal
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set());
  const [selectedTimes, setSelectedTimes] = useState<Set<string>>(new Set());
  const [callFormat, setCallFormat] = useState<'video' | 'phone'>('video');
  const [duration, setDuration] = useState<number>(30);

  const viewer = getCurrentUser();

  useEffect(() => {
    if (!id) return;
    investorService
      .getPitchDetail(id)
      .then((data) => {
        setPitch(data);
        setExpressed(data.isInterested ?? false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const submitSessionRequest = useCallback(async () => {
    if (!id || expressed || expressing) return;
    if (selectedDays.size === 0 || selectedTimes.size === 0) return;
    setExpressing(true);
    try {
      await investorService.requestSession(id, {
        days: Array.from(selectedDays),
        times: Array.from(selectedTimes),
        callFormat,
        duration,
      });
      setExpressed(true);
      setSessionModalOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setExpressing(false);
    }
  }, [id, expressed, expressing, selectedDays, selectedTimes, callFormat, duration]);

  // Generate next 5 upcoming business days (skip weekends)
  const availableDays = useMemo(() => {
    const days: { iso: string; weekday: string; date: string }[] = [];
    const start = new Date();
    let cursor = 1;
    while (days.length < 5 && cursor < 14) {
      const d = new Date(start);
      d.setDate(start.getDate() + cursor);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        days.push({
          iso: d.toISOString().slice(0, 10),
          weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
          date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        });
      }
      cursor++;
    }
    return days;
  }, []);

  const toggleInSet = (
    setState: React.Dispatch<React.SetStateAction<Set<string>>>,
    key: string
  ) => {
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetSessionForm = () => {
    setSelectedDays(new Set());
    setSelectedTimes(new Set());
    setCallFormat('video');
    setDuration(30);
  };

  const confirmPass = useCallback(() => {
    if (!id) return;
    const next = new Set(passedSet);
    next.add(id);
    writePassed(next);
    setPassedSet(next);
    setConfirmOpen(false);
    navigate('/app/dashboard/investor');
  }, [id, passedSet, navigate]);

  const unpass = useCallback(() => {
    if (!id) return;
    const next = new Set(passedSet);
    next.delete(id);
    writePassed(next);
    setPassedSet(next);
  }, [id, passedSet]);

  // Derived helper line — "Ask for Equity → Valuation pre-money"
  const dealSummary = useMemo(() => {
    if (!pitch) return '';
    const parts: string[] = [];
    if (pitch.askAmount) parts.push(pitch.askAmount);
    if (pitch.equityOffered) parts.push(`for ${pitch.equityOffered}`);
    if (parts.length === 0) return '';
    const left = parts.join(' ');
    if (pitch.preMoneyValuation) return `${left} → ${pitch.preMoneyValuation} pre-money`;
    return left;
  }, [pitch]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <p className="font-serif-display italic text-muted py-12">Loading pitch…</p>
      </div>
    );
  }

  if (!pitch) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <Link
          to="/app/dashboard/investor"
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
        >
          <BackArrow />
          Back to deal flow
        </Link>
        <div className="bg-shell border border-line rounded-card p-12 text-center">
          <p className="font-display text-2xl tracking-wide text-text uppercase mb-2">Pitch not found</p>
          <p className="font-serif-display italic text-muted">That pitch may have been withdrawn.</p>
        </div>
      </div>
    );
  }

  const startup = pitch.startupId || {};
  const cofounders = (pitch.startupId?.coFounders) || [];
  const startupName = pitch.startupId?.name || 'Startup';
  const industry = pitch.startupId?.industry || '';
  const stage = pitch.startupId?.stage || 'mvp';
  const founderName = pitch.userId?.name || '';

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* ─── Back link ─── */}
      <Link
        to="/app/dashboard/investor"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
      >
        <BackArrow />
        Back to deal flow
      </Link>

      {/* ─── Passed banner (only if deep-linked to a passed pitch) ─── */}
      {isPassed && (
        <div className="bg-warning/10 border border-warning/30 rounded-card p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div className="flex-1 text-sm">
            <span className="text-text">You marked this pitch as <span className="font-semibold">Not Interested</span>. It's hidden from your deal flow.</span>
            <button onClick={unpass} className="ml-2 text-chart hover:text-chart/80 font-medium underline transition-colors">
              Undo
            </button>
          </div>
        </div>
      )}

      {/* ─── Request-sent banner ─── */}
      {expressed && (
        <div className="bg-success/10 border border-success/30 rounded-card p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="flex-1 text-sm">
            <span className="text-text font-medium">Session requested.</span>{' '}
            <span className="text-muted">We'll coordinate an intro call with the founder within 24 hours and email you the details.</span>
          </div>
        </div>
      )}

      {/* ─── Hero band ─── */}
      <div className="bg-shell border border-line rounded-card p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          <div className="w-16 h-16 rounded-2xl bg-chart/10 border border-chart/20 flex items-center justify-center flex-shrink-0">
            <span className="text-chart font-bold text-2xl">{startupName.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Pitch Review</p>
            <h1 className="font-display text-3xl md:text-5xl tracking-wide text-text leading-tight">{startupName}</h1>
            {startup.tagline && (
              <p className="font-serif-display italic text-muted text-lg mt-2">{startup.tagline}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-4">
              {industry && (
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${industryColor[industry] || 'bg-surface text-muted border-line'}`}>
                  {industry}
                </span>
              )}
              {stage && (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-surface text-muted border border-line capitalize">
                  {stageLabel[stage] || stage}
                </span>
              )}
              {startup.teamSize != null && (
                <span className="text-xs text-muted">
                  · {startup.teamSize} team members
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-7 pt-6 border-t border-line grid grid-cols-1 sm:grid-cols-3 gap-5">
          <StatCell label="Ask" value={pitch.askAmount} />
          <StatCell label="Equity Offered" value={pitch.equityOffered} />
          <StatCell label="Pre-Money Val." value={pitch.preMoneyValuation} accent="chart" />
        </div>
        {dealSummary && (
          <p className="mt-4 text-xs italic text-muted">
            {dealSummary}
          </p>
        )}
      </div>

      {/* ─── Action bar ─── */}
      <div className="bg-shell border border-line rounded-card p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="text-sm">
          <span className="text-muted">Viewing as </span>
          <span className="text-text font-semibold">{viewer.name || 'Investor'}</span>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Not Interested */}
          <Tooltip content="Not interested in this pitch" position="top">
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={isPassed}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card border border-line bg-surface text-text text-sm font-medium hover:bg-black/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPassed ? 'Passed' : 'Not Interested'}
            </button>
          </Tooltip>

          {/* Request a Session */}
          <div className="flex flex-col items-stretch sm:items-end">
            <Tooltip content="Request to book an intro call with the founder" position="top">
              <button
                type="button"
                onClick={() => {
                  resetSessionForm();
                  setSessionModalOpen(true);
                }}
                disabled={expressing || expressed}
                className="w-full sm:w-auto px-5 py-2.5 rounded-card bg-chart text-black text-sm font-semibold hover:bg-chart/90 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {expressed ? 'Session Requested' : 'Request a Session'}
              </button>
            </Tooltip>
            {expressed ? (
              <p className="mt-1.5 text-[11px] italic text-success">
                ✓&nbsp;Intro call request sent to the founder.
              </p>
            ) : (
              <p className="mt-1.5 text-[11px] italic text-muted">
                *&nbsp;Request to book an intro call with the founder
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Startup description ─── */}
      {startup.description && (
        <section className="bg-shell border border-line rounded-card p-6">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-3">About the startup</div>
          <p className="text-text text-sm leading-relaxed">{startup.description}</p>
          {startup.website && (
            <a
              href={startup.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-chart hover:text-chart/80 text-sm font-medium transition-colors"
            >
              Visit website
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </section>
      )}

      {/* ─── 2-col sections ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SectionCard label="Problem" value={pitch.problem} />
        <SectionCard label="Solution" value={pitch.solution} />
        <SectionCard label="Market Size" value={pitch.marketSize} />
        <SectionCard label="Business Model" value={pitch.businessModel} />
        <SectionCard label="Traction" value={pitch.traction} accent />
        <SectionCard label="Competitive Advantage" value={pitch.competitiveAdvantage} />
        <SectionCard label="Financials" value={pitch.financials} />
        <SectionCard label="Use of Funds" value={pitch.useOfFunds} />
      </div>

      {/* ─── Team ─── */}
      {(pitch.team || cofounders.length > 0) && (
        <section className="bg-shell border border-line rounded-card p-6">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-4">Team</div>
          {pitch.team && !cofounders.length && (
            <p className="text-text text-sm leading-relaxed">{pitch.team}</p>
          )}
          {cofounders.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cofounders.map((person: any, i: number) => (
                <div key={i} className="bg-surface rounded-card p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-chart font-bold text-xs">{initialsOf(person.name || '?')}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-text font-medium text-sm truncate">{person.name}</p>
                    <p className="text-muted text-xs truncate">{person.role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ─── Pitch deck ─── */}
      {pitch.fileUrl && (
        <section className="bg-chart/5 border border-chart/20 rounded-card p-6 text-center">
          <div className="text-[10px] uppercase tracking-wider text-chart font-semibold mb-3">Pitch Deck</div>
          <svg className="w-10 h-10 text-chart mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <a
            href={pitch.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-chart font-semibold text-sm hover:text-chart/80 transition-colors"
          >
            View pitch deck
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </section>
      )}

      {/* ─── Confirm modal ─── */}
      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Pass on this pitch?">
        <div className="space-y-5">
          <p className="text-text text-sm leading-relaxed">
            <span className="font-semibold">{startupName}</span> will be hidden from your deal flow. You can undo this anytime from the dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card border border-line bg-shell text-text text-sm font-medium hover:bg-surface transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmPass}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card bg-danger text-white text-sm font-semibold hover:bg-danger/90 transition-colors shadow-sm"
            >
              Yes, pass
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── Request-a-Session modal ─── */}
      <Modal
        isOpen={sessionModalOpen}
        onClose={() => !expressing && setSessionModalOpen(false)}
        title={`Request a session${founderName ? ` with ${founderName}` : ''}`}
        className="max-w-lg"
      >
        <div className="space-y-5">
          <p className="text-muted text-sm leading-relaxed">
            Pick a few options — we'll coordinate with the founder on your behalf. No manual back-and-forth.
          </p>

          {/* Days */}
          <div>
            <div className="text-xs font-semibold text-text uppercase tracking-wider mb-2">
              Which days work for you?
              <span className="ml-2 text-[10px] font-normal text-muted normal-case tracking-normal">Select one or more</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {availableDays.map((d) => {
                const active = selectedDays.has(d.iso);
                return (
                  <button
                    key={d.iso}
                    type="button"
                    onClick={() => toggleInSet(setSelectedDays, d.iso)}
                    className={`px-2 py-2.5 rounded-card border text-center transition-all ${
                      active
                        ? 'bg-chart/15 border-chart text-chart font-semibold'
                        : 'bg-shell border-line text-text hover:border-chart/40'
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider">{d.weekday}</div>
                    <div className="text-sm tabular-nums mt-0.5">{d.date}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time of day */}
          <div>
            <div className="text-xs font-semibold text-text uppercase tracking-wider mb-2">
              Time of day
              <span className="ml-2 text-[10px] font-normal text-muted normal-case tracking-normal">Select one or more</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: 'morning', label: 'Morning', hint: '9 AM – 12 PM' },
                { key: 'afternoon', label: 'Afternoon', hint: '12 – 5 PM' },
                { key: 'evening', label: 'Evening', hint: '5 – 8 PM' },
              ].map((t) => {
                const active = selectedTimes.has(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => toggleInSet(setSelectedTimes, t.key)}
                    className={`px-3 py-2.5 rounded-card border text-left transition-all ${
                      active
                        ? 'bg-chart/15 border-chart text-chart'
                        : 'bg-shell border-line text-text hover:border-chart/40'
                    }`}
                  >
                    <div className="text-sm font-semibold">{t.label}</div>
                    <div className="text-[10px] text-muted mt-0.5">{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Format + Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-text uppercase tracking-wider mb-2">Format</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'video', label: 'Video' },
                  { key: 'phone', label: 'Phone' },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setCallFormat(f.key as 'video' | 'phone')}
                    className={`px-3 py-2.5 rounded-card border text-sm font-medium transition-all ${
                      callFormat === f.key
                        ? 'bg-chart/15 border-chart text-chart'
                        : 'bg-shell border-line text-text hover:border-chart/40'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-text uppercase tracking-wider mb-2">Duration</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: 15, label: '15 min' },
                  { val: 30, label: '30 min' },
                  { val: 60, label: '1 hour' },
                ].map((d) => (
                  <button
                    key={d.val}
                    type="button"
                    onClick={() => setDuration(d.val)}
                    className={`px-2 py-2.5 rounded-card border text-sm font-medium transition-all ${
                      duration === d.val
                        ? 'bg-chart/15 border-chart text-chart'
                        : 'bg-shell border-line text-text hover:border-chart/40'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Summary line */}
          {(selectedDays.size > 0 || selectedTimes.size > 0) && (
            <div className="bg-surface border border-line rounded-card p-3 text-xs text-muted leading-relaxed">
              <span className="text-[10px] uppercase tracking-wider text-text font-semibold">You're requesting:</span>{' '}
              <span className="text-text font-medium">{duration} min</span>{' '}
              <span>{callFormat === 'video' ? 'video' : 'phone'} call</span>
              {selectedDays.size > 0 && (
                <>
                  {' · '}
                  <span>{selectedDays.size} day{selectedDays.size !== 1 ? 's' : ''}</span>
                </>
              )}
              {selectedTimes.size > 0 && (
                <>
                  {' · '}
                  <span>{Array.from(selectedTimes).join(' / ')}</span>
                </>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={() => setSessionModalOpen(false)}
              disabled={expressing}
              className="w-full sm:w-auto px-5 py-2.5 rounded-card border border-line bg-shell text-text text-sm font-medium hover:bg-surface transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitSessionRequest}
              disabled={
                expressing || selectedDays.size === 0 || selectedTimes.size === 0
              }
              className="w-full sm:w-auto px-5 py-2.5 rounded-card bg-chart text-black text-sm font-semibold hover:bg-chart/90 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {expressing ? 'Sending…' : 'Send request'}
            </button>
          </div>

          {(selectedDays.size === 0 || selectedTimes.size === 0) && !expressing && (
            <p className="text-[11px] italic text-muted text-center">
              Pick at least one day and one time-of-day to continue.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PitchView;

// ── Icons ──────────────────────────────────────────────────────────────

const BackArrow: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);
